import { safeJson } from "@/lib/http";
import { supabase } from "@/lib/supabase";

export const THEMES = [
    { id: "social_rh", label: "Social / RH", questions: [
        { id: "remarques", label: "Remarques sociales éventuelles (effectifs, tensions, licenciements en cours, postes clés…)" },
    ] },
    { id: "activite", label: "Activité, marques et offre", questions: [
        { id: "description", label: "Décrivez votre activité avec vos propres mots" },
        { id: "marques", label: "Quelles sont vos marques principales ?" },
        { id: "offre", label: "Quels sont vos services/produits principaux ?" },
    ] },
    { id: "supply_chain", label: "Supply chain", questions: [
        { id: "fournisseurs", label: "Dépendance aux principaux fournisseurs, ruptures ou hausses de prix subies ?" },
        { id: "stocks", label: "Gestion des stocks (rotation, obsolescence, invendus) ?" },
        { id: "logistique", label: "Incidents logistiques, transport ou sous-traitance ayant impacté l'activité ?" },
    ] },
    { id: "juridique", label: "Juridique", questions: [
        { id: "litiges", label: "Litiges en cours (clients, fournisseurs, bailleur, administration) et montants en jeu ?" },
        { id: "garanties", label: "Cautions personnelles, nantissements, garanties données ?" },
        { id: "structure", label: "Évolutions récentes de la structure (associés, holding, cessions, conventions réglementées) ?" },
        { id: "notaire", label: "Notaire — nom et coordonnées (ou « N/A »)" },
        { id: "commissaire_comptes", label: "Commissaire aux comptes — nom (ou « N/A »)" },
        { id: "avocat", label: "Avocat — nom et coordonnées (ou « N/A »)" },
    ] },
    { id: "tresorerie", label: "Trésorerie", questions: [
        { id: "remarques", label: "Remarques éventuelles" },
    ] },
    { id: "fiabilite_comptable", label: "Fiabilité comptable", questions: [
        { id: "remarques", label: "Remarques éventuelles" },
    ] },
    { id: "marche_macro", label: "Marché / Macro", questions: [
        { id: "remarques", label: "Remarques éventuelles" },
    ] },
];

export function countReponses(q = {}) {
    let total = 0, done = 0;
    for (const t of THEMES) for (const qu of t.questions) { total++; if (q?.[t.id]?.[qu.id]?.trim()) done++; }
    return { done, total };
}

export function contributionsFor(q, themeId, questionId) {
    return Object.entries(q?._contributions || {})
        .map(([email, c]) => ({ email, value: c?.[themeId]?.[questionId], updated_at: c?.updated_at }))
        .filter((c) => c.value?.trim());
}

export function fetchPartage(dossierId) { return apiJson(`/api/dossiers/${dossierId}/partage`); }
export function setPartage(dossierId, actif) { return apiJson(`/api/dossiers/${dossierId}/partage`, { method: "PUT", body: JSON.stringify({ actif }) }); }
export function regenererPartage(dossierId) { return apiJson(`/api/dossiers/${dossierId}/partage/regenerer`, { method: "POST" }); }
export function renameDossier(dossierId, nom_mission) { return apiJson(`/api/dossiers/${dossierId}`, { method: "PATCH", body: JSON.stringify({ nom_mission }) }); }
export function deleteDossier(dossierId) { return apiJson(`/api/dossiers/${dossierId}`, { method: "DELETE" }); }
export function fetchInsights(dossierId) { return apiJson(`/api/dossiers/${dossierId}/insights`); }
export function addInsight(dossierId, texte) { return apiJson(`/api/dossiers/${dossierId}/insights`, { method: "POST", body: JSON.stringify({ texte }) }); }
export function removeInsight(dossierId, index) { return apiJson(`/api/dossiers/${dossierId}/insights/${index}`, { method: "DELETE" }); }
export function extractEtatCivil(dossierId) { return apiJson(`/api/ia/extract-etat-civil`, { method: "POST", body: JSON.stringify({ dossier_id: dossierId }) }); }
export function extractIdentiteDirigeant(dossierId) { return apiJson(`/api/ia/extract-identite-dirigeant`, { method: "POST", body: JSON.stringify({ dossier_id: dossierId }) }); }
export function extractComptesBancaires(dossierId) { return apiJson(`/api/ia/extract-comptes-bancaires`, { method: "POST", body: JSON.stringify({ dossier_id: dossierId }) }); }
export function extractRepartitionCapital(dossierId) { return apiJson(`/api/ia/extract-repartition-capital`, { method: "POST", body: JSON.stringify({ dossier_id: dossierId }) }); }
export async function extractAffiliations(dossierId) {
    const { job_id } = await apiJson(`/api/ia/affiliations/jobs`, { method: "POST", body: JSON.stringify({ dossier_id: dossierId }) });
    for (let k = 0; k < 45; k++) {
        await new Promise((r) => setTimeout(r, 2000));
        const jb = await apiJson(`/api/ia/affiliations/jobs/${job_id}?dossier_id=${encodeURIComponent(dossierId)}`);
        if (jb.stage === "done") return { affiliations: (jb.result && jb.result.affiliations) || jb.affiliations || {} };
        if (jb.stage === "error" || jb.error) throw new Error(jb.error || "Extraction des affiliations échouée");
    }
    throw new Error("Délai dépassé — réessayez.");
}

async function apiJson(path, init = {}) {
    const { data } = await supabase.auth.getSession();
    const token = data.session?.access_token;
    if (!token) throw new Error("Session expirée, reconnectez-vous.");
    const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}${path}`, { ...init, headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json", ...(init.headers || {}) } });
    const body = await safeJson(r);
    if (!r.ok) throw new Error(typeof body.detail === "string" ? body.detail : `Erreur ${r.status}`);
    return body;
}

export async function fetchQuestionnaire(dossierId) {
    const { data, error } = await supabase.from("dossiers").select("questionnaire_qualitatif").eq("id", dossierId).single();
    if (error) throw error;
    return data?.questionnaire_qualitatif ?? {};
}

export async function saveQuestionnaire(dossierId, questionnaire) {
    const { data: cur } = await supabase.from("dossiers").select("questionnaire_qualitatif").eq("id", dossierId).single();
    const payload = { ...questionnaire, _contributions: cur?.questionnaire_qualitatif?._contributions || questionnaire._contributions || {}, _meta: { ...(questionnaire._meta || {}), updated_at: new Date().toISOString() } };
    const { error } = await supabase.from("dossiers").update({ questionnaire_qualitatif: payload }).eq("id", dossierId);
    if (error) throw error;
    return payload;
}
